// Catalogue des composants ODICE de l'application, dans le thème courant.

import {
  AlertList,
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  DeltaBadge,
  Drawer,
  EmptyState,
  ErrorState,
  Field,
  FilterChip,
  IconButton,
  Input,
  KpiStrip,
  KpiTile,
  Meter,
  Modal,
  Notice,
  SectionHeader,
  SegmentedControl,
  Select,
  SourceTag,
  Spinner,
  StatusChip,
  Table,
  Tabs,
  Td,
  Th,
  type Tone,
} from '@cockpit/ui';
import { Bell, Info, Plus, Settings } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { DivergingBars, DsoChart, HBars, TrendChart, Waterfall } from '../components/charts.js';
import { PageHeader } from '../components/Layout.js';

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <SectionHeader title={title} />
      <Card className="flex flex-wrap items-center gap-3">{children}</Card>
    </section>
  );
}

const SWATCHES: [string, string][] = [
  ['navy-600', 'Bleu 2020 · action'],
  ['azur-500', 'Bleu Odice · accent, focus'],
  ['ink', 'Gris charcoal · texte'],
  ['gray-500', 'Gris moyen'],
  ['gray-300', 'Gris souris'],
  ['danger-600', 'Rouge · alerte uniquement'],
  ['warn-600', 'À surveiller'],
  ['success-600', 'Favorable'],
];

export function DesignPage() {
  const [tab, setTab] = useState<'a' | 'b' | 'c'>('a');
  const [seg, setSeg] = useState<'x' | 'y'>('x');
  const [chip, setChip] = useState(true);
  const [modal, setModal] = useState(false);
  const [drawer, setDrawer] = useState(false);
  return (
    <div className="flex flex-col gap-10">
      <PageHeader
        eyebrow="Design system ODICE"
        title="Catalogue des composants"
        sub="Charte graphique 2020, appliquée au cockpit."
      />

      <section className="flex flex-col gap-3">
        <SectionHeader title="Couleurs" />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SWATCHES.map(([t, l]) => (
            <Card key={t} padded={false} className="overflow-hidden">
              <div className="h-14" style={{ background: `rgb(var(--c-${t}))` }} />
              <p className="px-3 py-2 text-[12.5px] text-ink-2">{l}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeader title="Typographie" />
        <Card className="flex flex-col gap-2">
          <p className="font-display text-[12px] uppercase tracking-[0.18em] text-ink-3">
            Surtitre · Gilroy Regular
          </p>
          <p className="font-display text-[34px] font-bold leading-tight">Titre de page · Gilroy Bold</p>
          <p className="font-display text-[18px] text-ink-2">Sous-titre · Gilroy Regular</p>
          <p className="max-w-[65ch] text-[15px]">
            Texte courant en Brandon Grotesque. Nous intervenons dans toute la France au travers de plusieurs
            entités, pour l’installation et la maintenance des cuisines professionnelles.
          </p>
        </Card>
      </section>

      <Block title="Boutons">
        <Button icon={<Plus size={18} strokeWidth={1.75} />}>Créer un import</Button>
        <Button variant="secondary">Annuler</Button>
        <Button variant="ghost">Effacer</Button>
        <Button variant="danger">Supprimer</Button>
        <Button disabled>Désactivé</Button>
        <Button size="sm">Petit bouton</Button>
        <IconButton label="Notifications">
          <Bell size={20} strokeWidth={1.75} />
        </IconButton>
        <IconButton label="Réglages" active>
          <Settings size={20} strokeWidth={1.75} />
        </IconButton>
        <Avatar name="Clémentine Rousseau" />
      </Block>

      <Block title="Badges et statuts">
        {(['neutral', 'navy', 'azur', 'success', 'warn', 'danger'] as Tone[]).map((t) => (
          <Badge key={t} tone={t} dot>
            {t}
          </Badge>
        ))}
        <StatusChip status="ok">En ligne</StatusChip>
        <StatusChip status="watch">À surveiller</StatusChip>
        <StatusChip status="off">Décroché</StatusChip>
        <SourceTag source="FIN" date="31/07/2026" />
        <SourceTag source="OPE" date="29/09/2026" />
        <DeltaBadge tone="good" label="vs budget">
          +1,52 M€ (+11 %)
        </DeltaBadge>
        <DeltaBadge tone="bad" label="vs budget">
          −0,40 M€ (−8 %)
        </DeltaBadge>
        <DeltaBadge tone="flat">0,00 M€</DeltaBadge>
      </Block>

      <Block title="Filtres et navigation">
        <FilterChip active={chip} onClick={() => setChip(!chip)} count={12}>
          Solde à facturer
        </FilterChip>
        <SegmentedControl
          label="Exemple"
          value={seg}
          onChange={setSeg}
          options={[
            { key: 'x', label: 'Rattrapables', count: 18 },
            { key: 'y', label: 'Constatées', count: 7 },
          ]}
        />
        <div className="w-full">
          <Tabs
            label="Exemple"
            value={tab}
            onChange={setTab}
            tabs={[
              { key: 'a', label: 'Actions à mener', count: 294 },
              { key: 'b', label: 'Dérives de marge', count: 72 },
              { key: 'c', label: 'Dates à vérifier' },
            ]}
          />
        </div>
      </Block>

      <Block title="Formulaires">
        <Field label="Adresse e-mail" hint="Votre adresse professionnelle." className="w-64">
          {(id) => <Input id={id} placeholder="prenom.nom@odice.fr" />}
        </Field>
        <Field label="Entité" className="w-64">
          {(id) => (
            <Select id={id}>
              <option>Martinon</option>
              <option>Provence Froid</option>
            </Select>
          )}
        </Field>
        <Field label="Montant" error="Saisissez un nombre positif." className="w-64">
          {(id) => <Input id={id} defaultValue="-12" />}
        </Field>
      </Block>

      <section className="flex flex-col gap-3">
        <SectionHeader title="Indicateurs" source={<SourceTag source="FIN" />} />
        <KpiStrip>
          <KpiTile
            label="Chiffre d’affaires"
            value="14,94 M€"
            delta={
              <DeltaBadge tone="good" label="vs budget">
                +1,52 M€
              </DeltaBadge>
            }
          />
          <KpiTile label="Marge brute" value="4,56 M€" sub="30,5 % du CA" />
          <KpiTile label="Priorité critique" value="60" sub="enjeu ≥ 50 k€" alert />
          <KpiTile label="DSO" value="55 j" sub="objectif 60 j" />
        </KpiStrip>
        <AlertList
          items={[
            { severity: 'bad', text: 'Trésorerie en baisse de 1,14 M€ depuis décembre' },
            { severity: 'warn', text: 'DSO à 88 j, soit 28 j au-dessus de l’objectif' },
            { severity: 'good', text: 'Aucun signal sur les autres indicateurs' },
          ]}
        />
        <Card className="flex flex-col gap-3">
          <Meter label="Exemple" value={63} marker={58} />
          <Meter label="Exemple" value={41} marker={58} tone="warn" />
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Tendance cumulée" />
          <TrendChart
            actual={[1.6, 3.1, 4.8, 6.9, 9.4, 11.9, 14.9]}
            budget={[1.9, 3.8, 5.8, 7.7, 9.6, 11.5, 13.4]}
            months={[1, 2, 3, 4, 5, 6, 7]}
            selected={5}
            fmt={(n) => `${n.toFixed(1)} M€`}
          />
        </Card>
        <Card>
          <CardHeader title="DSO" />
          <DsoChart
            series={[2, 3, 4, 5, 6, 7, 8, 9].map((m, i) => ({
              month: m,
              dso: [54, 116, 118, 49, 49, 55, 63, 56][i]!,
            }))}
            target={60}
          />
        </Card>
        <Card>
          <CardHeader title="Barres par entité" />
          <HBars
            fmt={(n) => `${n.toFixed(2)} M€`}
            items={[
              { key: 'a', label: 'Martinon', value: 1.4, ref: 0.52, good: true },
              { key: 'b', label: 'Dimco', value: -0.2, ref: 0.3, good: false },
            ]}
          />
        </Card>
        <Card>
          <CardHeader title="Décomposition et pont" />
          <DivergingBars
            fmt={(n) => `${n} €`}
            rows={[
              { label: 'Achats', value: -10739 },
              { label: 'MO + ST', value: -301 },
              { label: 'Autres charges', value: 1256 },
            ]}
          />
          <div className="mt-6">
            <Waterfall
              fmt={(n) => n.toFixed(1)}
              steps={[
                { label: 'Début', value: 1.5, total: true },
                { label: 'CAF', value: 0.5 },
                { label: 'BFR', value: -0.7 },
                { label: 'Fin', value: 1.3, total: true },
              ]}
            />
          </div>
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <SectionHeader title="Tableau" />
        <Card padded={false} className="px-2 py-3">
          <Table minWidth={480}>
            <thead>
              <tr>
                <Th>Indicateur</Th>
                <Th align="right">Réalisé</Th>
                <Th>Statut</Th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <Td>CA total</Td>
                <Td align="right">14,94 M€</Td>
                <Td>
                  <StatusChip status="ok">En ligne</StatusChip>
                </Td>
              </tr>
              <tr>
                <Td>REX</Td>
                <Td align="right">1,41 M€</Td>
                <Td>
                  <StatusChip status="watch">À surveiller</StatusChip>
                </Td>
              </tr>
            </tbody>
          </Table>
        </Card>
      </section>

      <Block title="Retours et superpositions">
        <Spinner />
        <Button variant="secondary" onClick={() => setModal(true)}>
          Ouvrir une modale
        </Button>
        <Button variant="secondary" onClick={() => setDrawer(true)}>
          Ouvrir un panneau
        </Button>
        <div className="flex w-full flex-col gap-3">
          <Notice icon={<Info size={18} strokeWidth={1.75} />}>
            Information neutre sur la provenance des données.
          </Notice>
          <ErrorState title="Import refusé">Le fichier n’est pas un JSON valide.</ErrorState>
          <EmptyState title="Aucune affaire ne correspond à ces filtres">
            Élargissez la sélection ou retirez un filtre.
          </EmptyState>
        </div>
      </Block>

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title="Modale"
        footer={<Button onClick={() => setModal(false)}>Fermer</Button>}
      >
        <p className="text-[14px]">Contenu de la modale.</p>
      </Modal>
      <Drawer
        open={drawer}
        onClose={() => setDrawer(false)}
        title="Panneau latéral"
        subtitle="Détail d’une affaire"
      >
        <p className="text-[14px]">Contenu du panneau.</p>
      </Drawer>
    </div>
  );
}
